import React, { useEffect } from 'react';
import { ArrowLeft, Heart, User, Truck } from 'lucide-react';
import { useFamilyStore } from '../stores/familyStore';
import Timeline from '../components/Timeline';

const FamilyDashboard: React.FC = () => {
  const { ordemServico, limparOS } = useFamilyStore();

  useEffect(() => {
    // Limpar OS quando sair da página..
    return () => {
      // Não limpar automaticamente para manter o estado
    };
  }, []);

  if (!ordemServico) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Concluída':
        return 'bg-green-100 text-green-800';
      case 'Em Andamento':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Concluída':
        return 'Serviço Concluído';
      case 'Em Andamento':
        return 'Em Andamento';
      default:
        return 'Pendente';
    }
  };

  const fasesAtivas = ordemServico.fases.filter(f => f.status_fase === 'Em Rota');
  const fasesConcluidas = ordemServico.fases.filter(f => f.status_fase === 'Concluída');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 py-3 sm:py-0 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
              <button
                onClick={limparOS}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                title="Voltar ao código de acesso"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1 sm:flex-initial">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                    Portal da Família
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">
                    Acompanhamento em tempo real
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 w-full sm:w-auto justify-between sm:justify-end">
              <div className="text-left sm:text-right min-w-0 flex-1 sm:flex-initial">
                <p className="text-xs sm:text-sm text-gray-500 truncate">
                  {ordemServico.numero_nota_contratacao ? `N.C. ${ordemServico.numero_nota_contratacao}` : `OS #${ordemServico.codigo_acesso}`}
                </p>
                <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                  {ordemServico.nome_falecido}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          
          {/* Card de Informações da OS */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Informações do Serviço
              </h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ordemServico.status_geral)}`}>
                {getStatusText(ordemServico.status_geral)}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Falecido</p>
                  <p className="font-medium text-gray-900">{ordemServico.nome_falecido}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Truck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Código OS</p>
                  <p className="font-medium text-gray-900">
                    {ordemServico.numero_nota_contratacao 
                      ? `N.C. ${ordemServico.numero_nota_contratacao}` 
                      : `#${ordemServico.codigo_acesso}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Timeline das Fases */}
          <Timeline fases={ordemServico.fases} />
        </div>
      </div>
    </div>
  );
};

export default FamilyDashboard;
